import { useState, useEffect, useMemo } from 'react';
import { Trash2 } from 'lucide-react';
import { useTheme } from '@/contexts/ThemeContext';
import { useAppData } from '@/contexts/AppDataContext';
import { useStore } from '@/contexts/StoreContext';
import { secureGetItem, secureRemoveItem } from '@/lib/storage';
import { HomeSectionTitle } from './HomeSectionTitle';
import { VehicleCard } from './VehicleCard';
import './styles.css';

interface RecentlyViewedVehiclesProps {
  config: any;
}

interface ViewedVehicle {
  id: number;
  timestamp: number;
}

export const RecentlyViewedVehicles = ({ config }: RecentlyViewedVehiclesProps) => {
  const { vehicles } = useAppData();
  const { selectedStore } = useStore();
  const { colors } = useTheme();
  const titleColor = colors.title;
  const buttonColor = colors.button;
  const [recentlyViewed, setRecentlyViewed] = useState<any[]>([]);

  const shouldFilterByStore = useMemo(() => {
    const unifiedAds = config?.unifiedAds !== false;
    const unifiedAdsStore = config?.unifiedAdsStore;
    const selectedStoreId = selectedStore?._id;

    if (!unifiedAds) return true;
    if (unifiedAdsStore === null || unifiedAdsStore === undefined) return false;
    return selectedStoreId !== unifiedAdsStore;
  }, [config?.unifiedAds, config?.unifiedAdsStore, selectedStore?._id]);

  useEffect(() => {
    const loadRecentlyViewed = async () => {
      try {
        const viewedStr = await secureGetItem('recentlyViewedVehicles');
        if (!viewedStr) return;

        const viewedIds: ViewedVehicle[] = JSON.parse(viewedStr);
        const sortedIds = viewedIds
          .sort((a, b) => b.timestamp - a.timestamp)
          .slice(0, 4);

        let viewedVehicles = sortedIds
          .map((viewed) => {
            const vehicle = vehicles.find((v) => v.id === viewed.id);
            return vehicle ? { ...vehicle, timestamp: viewed.timestamp } : null;
          })
          .filter((vehicle) => vehicle !== null);

        if (shouldFilterByStore && selectedStore?._id) {
          viewedVehicles = viewedVehicles.filter((vehicle) => vehicle.store === selectedStore._id);
        }

        setRecentlyViewed(viewedVehicles);
      } catch (error) {
        console.error('Erro ao carregar veículos vistos recentemente:', error);
      }
    };

    loadRecentlyViewed();
    const handleStorageChange = () => loadRecentlyViewed();
    window.addEventListener('vehicleViewed', handleStorageChange);
    return () => window.removeEventListener('vehicleViewed', handleStorageChange);
  }, [vehicles, shouldFilterByStore, selectedStore?._id]);

  const clearRecentlyViewed = async () => {
    await secureRemoveItem('recentlyViewedVehicles');
    setRecentlyViewed([]);
    window.dispatchEvent(new Event('vehicleViewed'));
  };

  if (recentlyViewed.length === 0) return null;

  return (
    <section className="wl5-av-recent">
      <div className="wl5-container">
        <div className="wl5-av-recent-head">
          <HomeSectionTitle
            eyebrow="Seu histórico"
            titleBefore="Vistos"
            titleAccent="recentemente"
            accentColor={buttonColor}
            titleColor={titleColor}
            className="wl5-home-section-head--left"
          />
          <button
            type="button"
            onClick={clearRecentlyViewed}
            className="wl5-av-recent-clear"
            style={{ color: buttonColor, borderColor: `${buttonColor}30` }}
          >
            <Trash2 className="h-4 w-4" />
            <span className="hidden sm:inline">Limpar</span>
          </button>
        </div>

        <div className="wl5-vehicle-grid wl5-vehicle-grid--recent">
          {recentlyViewed.map((vehicle) => (
            <VehicleCard key={vehicle.id} vehicle={vehicle} />
          ))}
        </div>
      </div>
    </section>
  );
};
